import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery } from '@material-ui/core';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';

import colores from '../../Impresion/Bandeja/coloresBandeja';

/** Pasos que se muestran mientras "lee" la factura (los mismos que hace SmartScan). */
const PASOS = [
  'Leyendo el QR de AFIP',
  'Extrayendo cabecera, líneas y pie',
  'Verificando que neto + impuestos + percepciones = total',
  'Buscando los ítems de compra del proveedor'
];
const DURACION_PASO_MS = 1100;

const useStyles = makeStyles(() => ({
  zona: {
    border: `2px dashed ${colores.borde}`,
    borderRadius: '8px',
    padding: '36px 20px',
    textAlign: 'center',
    color: colores.secundario,
    cursor: 'pointer',
    transition: 'all .2s',
    '&:hover': {
      borderColor: colores.primario,
      backgroundColor: colores.primarioClaro
    }
  },
  arrastrando: {
    borderColor: colores.primario,
    backgroundColor: colores.primarioClaro
  },
  nota: {
    marginTop: '12px',
    fontSize: '12px',
    color: colores.tenue
  },
  paso: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 0',
    fontSize: '14px'
  },
  archivo: {
    marginBottom: '12px',
    fontWeight: 500,
    wordBreak: 'break-all'
  }
}));

/**
 * Subida de una factura (simulada): se puede arrastrar o elegir cualquier PDF, pero la demo siempre "lee" la
 * próxima factura de ejemplo. Al terminar los pasos llama a onSubir, que la agrega a la bandeja.
 */
const CargaSimulada = ({ abierto, quedanPorSubir, onCerrar, onSubir }) => {
  const classes = useStyles();
  const pantallaCompleta = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const input = useRef(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [paso, setPaso] = useState(-1);

  useEffect(() => {
    if (!abierto) {
      setArchivo(null);
      setPaso(-1);
    }
  }, [abierto]);

  useEffect(() => {
    if (paso < 0) return undefined;
    const timer = setTimeout(() => {
      if (paso < PASOS.length - 1) setPaso(paso + 1);
      else onSubir();
    }, DURACION_PASO_MS);
    return () => clearTimeout(timer);
  }, [paso]);

  const procesar = (nombre) => {
    setArchivo(nombre || 'factura.pdf');
    setPaso(0);
  };

  const soltar = (event) => {
    event.preventDefault();
    setArrastrando(false);
    const elegido = event.dataTransfer.files && event.dataTransfer.files[0];
    procesar(elegido && elegido.name);
  };

  return (
    <Dialog open={abierto} onClose={paso < 0 ? onCerrar : undefined} fullWidth maxWidth="sm" fullScreen={pantallaCompleta}>
      <DialogTitle>Subir factura de compra</DialogTitle>
      <DialogContent>
        {!quedanPorSubir ? (
          <div className={classes.nota} style={{ fontSize: '14px' }}>
            En esta demostración ya se subieron todas las facturas de ejemplo. Recargá la página para empezar de nuevo.
          </div>
        ) : paso < 0 ? (
          <React.Fragment>
            <div
              className={`${classes.zona} ${arrastrando ? classes.arrastrando : ''}`}
              onClick={() => input.current.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setArrastrando(true);
              }}
              onDragLeave={() => setArrastrando(false)}
              onDrop={soltar}
            >
              <CloudUploadOutlinedIcon style={{ fontSize: 48, color: colores.primario }} />
              <div>Arrastrá el PDF de la factura acá o hacé clic para elegirlo</div>
            </div>
            <input
              ref={input}
              type="file"
              accept="application/pdf,image/*"
              style={{ display: 'none' }}
              onChange={(event) => procesar(event.target.files[0] && event.target.files[0].name)}
            />
            <div className={classes.nota}>
              También se pueden recibir automáticamente desde una casilla de mail o una carpeta compartida.
              <br />
              Demo: se usa una factura de ejemplo, el archivo elegido no se lee.
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className={classes.archivo}>{archivo}</div>
            {PASOS.map((texto, indice) => (
              <div key={texto} className={classes.paso} style={{ color: indice > paso ? colores.tenue : undefined }}>
                {indice < paso ? (
                  <CheckCircleIcon style={{ color: colores.ok }} />
                ) : indice === paso ? (
                  <CircularProgress size={22} />
                ) : (
                  <RadioButtonUncheckedIcon />
                )}
                {texto}
              </div>
            ))}
          </React.Fragment>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar} disabled={paso >= 0}>
          {quedanPorSubir ? 'Cancelar' : 'Cerrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CargaSimulada;
